import { Mindentity } from '@/components/Mindentity/Mindentity';
import { Typography } from '@/shared';
import { MultilinkStoryblok } from '@/shared/types/storyblok/storyblok-types';
import { Icon } from '@/shared/ui/Icon/Icon';
import clsx from 'clsx';
import { micromark } from 'micromark';
import dynamic from 'next/dynamic';
import router from 'next/router';
import { useRef } from 'react';
import AuthorArticles from '../Articles/AuthorArticles';
import styles from './SelectedContent.module.scss';
import { SBImage } from '@/components/ImageService/SBImage';
import { useMicrocopy } from '@/shared/providers/Microcopy/MicrocopyHook';

// const fallbackImage = process.env.fallback_image;

const joinedRole = (role: string | string[]) => (Array.isArray(role) ? role.join(' & ') : role);

const SelectContent = ({ selectedContent, setSelectedContent, isDark, featuredArticleButton }: any) => {
    const selectedRef = useRef<HTMLDivElement>(null);
    const handleClose = () => {
        setSelectedContent('');
    };
    const markdown = micromark(selectedContent.bio ?? '');
    const { microcopy } = useMicrocopy();

    const DynamicAuthor = dynamic(() => import('../Articles/AuthorArticles'), {
        loading: () => <AuthorArticles author={selectedContent._uid} featuredArticleButton={featuredArticleButton} />,
    });

    return (
        <div ref={selectedRef} className={clsx(styles.selectedRoot, { [styles.dark]: isDark })}>
            <div className={styles.closeBtn}>
                <Icon onClick={handleClose} name="close" className={styles.close}></Icon>
            </div>
            <div className={styles.selected}>
                {selectedContent.portrait && (
                    <div className={styles.selectedImage}>
                        <SBImage image={selectedContent.portrait} height={400} className={styles.image} />
                    </div>
                )}
                <div className={styles.images}>
                    <div className={styles.identity}>
                        <Mindentity
                            name={selectedContent.fullname}
                            color={selectedContent.mind_color?.color}
                            isDark={isDark}
                            hover={false}
                        >
                            <picture />
                        </Mindentity>
                    </div>

                    {selectedContent?.imageGallery?.[0] && (
                        <div className={styles.gallery}>
                            <SBImage image={selectedContent.imageGallery[0]} height={288} width={288} />
                        </div>
                    )}
                </div>
                <div className={styles.contact}>
                    <Typography.H3 darkTheme={isDark} className={styles.title}>
                        {selectedContent.fullname}
                    </Typography.H3>
                    <Typography.Small darkTheme={isDark} className={styles.description}>
                        {joinedRole(selectedContent.role || '')}
                        {selectedContent.role && (selectedContent.City || selectedContent.location.name) && ' · '}
                        {selectedContent.mindLocation &&
                            `${selectedContent.City}${
                                (selectedContent.City || selectedContent.mindLocation.name) && ','
                            } ${selectedContent.mindLocation.name}`}
                    </Typography.Small>

                    {selectedContent?.email && (
                        <Typography.Link
                            darkTheme={isDark}
                            href={`mailto:${selectedContent?.email}`}
                            className={styles.email}
                        >
                            {selectedContent?.email}
                        </Typography.Link>
                    )}
                    {selectedContent?.phoneNumber && (
                        <Typography.Link
                            darkTheme={isDark}
                            href={`tel:${selectedContent?.phoneNumber}`}
                            className={styles.phone}
                        >
                            {selectedContent?.phoneNumber}
                        </Typography.Link>
                    )}
                    {selectedContent?.bio && (
                        <div className={styles.bio}>
                            <div style={{ color: 'white' }} dangerouslySetInnerHTML={{ __html: markdown }} />
                        </div>
                    )}
                    {Array.isArray(selectedContent?.certificates) && selectedContent?.certificates.length > 0 && (
                        <>
                            <Typography.H4 darkTheme>{microcopy.achievements}</Typography.H4>
                            <ul className={styles.certificates}>
                                {selectedContent?.certificates?.map((item: MultilinkStoryblok) => {
                                    if (!item || !item.link) return null;

                                    return (
                                        <li
                                            key={item._uid}
                                            onClick={() =>
                                                item?.link?.url
                                                    ? item?.link?.target === '_blank'
                                                        ? window.open(item.link.url, '_blank')
                                                        : router.push(item.link.url)
                                                    : null
                                            }
                                        >
                                            <span className={clsx(item?.link?.url ? styles.link : styles.noLink)}>
                                                {item.name}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
                <div className={styles.featuredArticle}>
                    <DynamicAuthor author={selectedContent._uid} featuredArticleButton={featuredArticleButton} />
                </div>
            </div>
        </div>
    );
};

export default SelectContent;
